import { TimePicker } from 'antd';
import momentTz from 'moment-timezone';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import { FORTA_BLACK, FORTA_BLUE, FORTA_ERROR, FORTA_GRAY2, FORTA_WHITE } from '../../utils/colors';
import FHTooltip from './FHTooltip';

import type { TimePickerProps } from 'antd';

const updateScrollPositionOnOpen = (open: boolean) => {
  if (open) {
    // Wait for the dropdown to render
    setTimeout(() => {
      const columns = document.querySelectorAll('.ant-picker-time-panel-column');
      columns.forEach((column) => {
        if (!(column instanceof HTMLElement)) return;

        const selectedItem = column.querySelector<HTMLElement>(
          '.ant-picker-time-panel-cell-selected',
        );
        if (selectedItem) {
          // Set the scroll position to the selected item
          column.scrollTop =
            selectedItem.offsetTop - column.clientHeight / 2 + selectedItem.clientHeight / 2;
        }
      });
    }, 100);
  }
};

interface IFAntdTimePickerProps extends TimePickerProps {
  inputHeight?: number;
  error?: boolean;
  coloredBackground?: boolean;
  hideTimezoneDisplay?: boolean;
  tooltipBackground?: string;
  enableContentTooltip?: boolean;
  showClockIcon?: boolean;
}

const StyledAntdTimePicker = styled(TimePicker, {
  shouldForwardProp: (prop) =>
    prop !== 'inputHeight' &&
    prop !== 'error' &&
    prop !== 'coloredBackground' &&
    prop !== 'hideTimezoneDisplay' &&
    prop !== 'hideTimezoneDisplay' &&
    prop !== 'showClockIcon',
})<IFAntdTimePickerProps>(
  ({ coloredBackground = true, error, inputHeight, hideTimezoneDisplay }) => ({
    cursor: 'pointer',
    borderRadius: hideTimezoneDisplay ? '10px' : '10px 0 0 10px',
    height: inputHeight ? inputHeight : 30,
    padding: '0 0.5em',
    backgroundColor: coloredBackground ? FORTA_BLUE.light3 : FORTA_WHITE,

    border: error ? `2px solid ${FORTA_ERROR}` : `1px solid ${FORTA_BLUE.light1}`,
    borderRight: hideTimezoneDisplay ? `1px solid ${FORTA_BLUE.light1}` : 'none !important',
    '&:focus-within': {
      border: error ? `2px solid ${FORTA_ERROR}` : `1px solid ${FORTA_BLUE.light1}`,
    },

    '&:hover': {
      border: error ? `2px solid ${FORTA_ERROR}` : `1px solid ${FORTA_BLUE.light1}`,
    },

    '& .ant-picker-suffix': {
      color: FORTA_BLUE.primary,
    },

    '&.ant-picker-disabled': {
      border: `1px solid ${FORTA_BLUE.light1}`,
      '& .ant-picker-input>input[disabled]': {
        color: FORTA_BLACK,
      },
      '& .ant-picker-suffix': {
        color: FORTA_BLUE.primary,
        opacity: 0.5,
      },
    },
  }),
);

const userTimezone = momentTz.tz.guess(true);
const nowInUserTimezone = momentTz().tz(userTimezone);

const FAntdTimePicker = (props: IFAntdTimePickerProps) => {
  const { hideTimezoneDisplay, tooltipBackground, showClockIcon = false } = props;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <StyledAntdTimePicker
        {...props}
        suffixIcon={
          showClockIcon ? (
            <AccessTimeIcon sx={{ color: FORTA_BLUE.primary, width: 18, height: 18 }} />
          ) : null
        }
        onOpenChange={updateScrollPositionOnOpen}
        renderExtraFooter={() => (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography
              sx={{
                height: 40,
                cursor: 'pointer',
                p: 1,
                fontSize: 12,
                borderLeft: 'none',
                backgroundColor: FORTA_WHITE,
              }}
            >
              {`${userTimezone} (${nowInUserTimezone.format('z')})`}
            </Typography>
          </Box>
        )}
      />
      {!hideTimezoneDisplay && (
        <FHTooltip
          arrow
          title={`Your current timezone is ${userTimezone} (${nowInUserTimezone.format('z')})`}
          sx={{
            borderRadius: '8px',
            display: 'flex',
            padding: '10px',
            maxWidth: '370px',
          }}
          tooltipBoxShadow={'0px 4px 4px rgba(0, 0, 0, 0.25)'}
          tooltipbackground={tooltipBackground ? tooltipBackground : FORTA_WHITE}
        >
          <Typography
            sx={{
              height: 40,
              cursor: 'pointer',
              p: 1,
              borderRadius: '0 10px 10px 0',
              border: `1px solid ${FORTA_BLUE.light1}`,
              borderLeft: 'none',
              backgroundColor: FORTA_GRAY2,
            }}
          >
            {nowInUserTimezone.format('z')}
          </Typography>
        </FHTooltip>
      )}
    </Box>
  );
};

export default FAntdTimePicker;
